import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import React, { useState } from 'react';
import type { CSSProp } from 'styled-components';

import type { PictureSource } from './PictureSource';
import * as styles from './Picture.styles';

export interface PictureProps extends ComponentPropsWithoutRef<'picture'> {
  alt?: string;
  children?: ReactElement<typeof PictureSource> | ReactElement<typeof PictureSource>[];
  className?: string;
  fetchPriority?: 'auto' | 'high' | 'low';
  imgCss?: CSSProp;
  lazyLoading?: boolean;
  onError?: () => void;
  onLoad?: () => void;
  src: string;
}

export const Picture = ({
  alt = '',
  children,
  className,
  fetchPriority,
  imgCss,
  lazyLoading = false,
  onError,
  onLoad,
  src,
}: PictureProps) => {
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const onImageError = () => {
    setShowPlaceholder(false);
    onError?.();
  };

  const onImageLoad = () => {
    setShowPlaceholder(false);
    onLoad?.();
  };

  // React 18.x only applies the lowercase `fetchpriority` DOM attribute (the camelCase form is a
  // React 19 feature and warns/drops here), so spread it as the lowercase attribute the browser reads.
  const fetchPriorityAttr: Record<string, string> | undefined = fetchPriority
    ? { fetchpriority: fetchPriority }
    : undefined;

  return (
    <picture className={className}>
      {children}
      <img
        {...fetchPriorityAttr}
        alt={alt}
        css={[showPlaceholder && styles.imagePlaceholder, imgCss]}
        loading={lazyLoading ? 'lazy' : 'eager'}
        onError={onImageError}
        onLoad={onImageLoad}
        src={src}
      />
    </picture>
  );
};
